/**
 * What a room is configured as, and what it is when nothing was configured.
 *
 * One shape read by two very different callers: the administration routes, which
 * write it, and the way in, which reads it. It is here rather than beside either
 * of them because a configuration written under one set of rules and read under
 * another is two configurations.
 *
 * Every field is optional to send and none is optional to have: a room that was
 * never declared is a room at its defaults, and that is what the entry path has
 * always done. So a partial row, a null, a column the deployed database does not
 * have yet and a read that failed all land on the same answer — the defaults —
 * rather than on four.
 */

import { z } from "zod";
import {
  AudioTransportSchema,
  RoomModeSchema,
  VideoTransportSchema,
  type AudioTransport,
  type RoomMode,
  type VideoTransport,
} from "./roomControl";

/**
 * The longest a display name may be, matched by `rooms_display_name_check`.
 *
 * A name is a line on the room's strip beside a count and the controls, so the
 * ceiling is what fits there rather than what Postgres would carry. Changing it
 * means changing the constraint in the same commit.
 */
export const ROOM_DISPLAY_NAME_MAX = 60;

/** What the room is called, where somebody has said. */
export const RoomDisplayNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(ROOM_DISPLAY_NAME_MAX);

export interface RoomConfig {
  mode: RoomMode;
  /**
   * The name the room greets a visitor by, or null where it has none and the
   * code does the naming — `roomIdentity.roomName` is what that looks like.
   */
  displayName: string | null;
  /** Whether the room's tour starts without anybody pressing anything. */
  autostartTour: boolean;
  /**
   * Whether anybody but the host is offered the room's journal. Read here so a
   * room reopened by its owner opens the way they left it; the host changes it
   * live through `roomControl.ts`.
   */
  journalVisibleToAll: boolean;
  /** Whether the room's voices go through ai-coustics; the host's to change. */
  voiceCleaning: boolean;
  /**
   * Which plane each of the room's two media travels on. Read here for the
   * reason the journal's visibility is: they are the host's decisions about
   * this room, and a room everybody has left has nobody left to broadcast them.
   */
  videoTransport: VideoTransport;
  audioTransport: AudioTransport;
}

/**
 * What a room is when nothing has been declared about it.
 *
 * Campfire because that is what the product is and what every room made from
 * the entry screen opens as; no name because the code is already one; no tour
 * because a room that talks to you unbidden is a demo room, and being one is a
 * decision somebody makes room by room.
 */
export const DEFAULT_ROOM_CONFIG: RoomConfig = {
  mode: "campfire",
  displayName: null,
  autostartTour: false,
  journalVisibleToAll: true,
  voiceCleaning: true,
  videoTransport: "mesh",
  audioTransport: "mesh",
};

/**
 * How often a participant reads the room's record again on its own.
 *
 * Reading is how every setting arrives, and the interval is the one way that
 * needs nothing but the server answering: a room keeps working with realtime
 * down, and a client on another version hears what a broadcast in this
 * version's vocabulary would not tell it. `room-settings-changed` is the fast
 * path in front of it, so this bounds how late a setting can be, not how late
 * it usually is. The lookup route limits a whole address rather than a person,
 * and entering a room spends the same budget, so the interval is long enough
 * for a room of a few dozen behind one office or carrier address to stay well
 * inside it (`RATE_LIMIT` in `src/routes/api/room-lookup.ts`).
 */
export const ROOM_SETTINGS_REREAD_MS = 30_000;

/**
 * The row as the database holds it. Everything is optional and everything may
 * be null: the migration is applied by hand and separately
 * (`supabase/AGENTS.md`), so meeting a database without these columns is
 * ordinary rather than exceptional.
 */
const RoomConfigRowSchema = z.object({
  room_mode: z.unknown().optional(),
  display_name: z.unknown().optional(),
  autostart_tour: z.unknown().optional(),
  journal_visible_to_all: z.unknown().optional(),
  voice_cleaning: z.unknown().optional(),
  video_transport: z.unknown().optional(),
  audio_transport: z.unknown().optional(),
});

/** The columns a read of the configuration asks for, in one place. */
export const ROOM_CONFIG_COLUMNS =
  "code, room_mode, display_name, autostart_tour, journal_visible_to_all, voice_cleaning, video_transport, audio_transport";

/**
 * Reads a row into a configuration, falling back field by field.
 *
 * Field by field rather than all or nothing: a row with a name this version does
 * not understand still knows what kind of room it is, and dropping the whole
 * configuration over one unreadable field would lose what was readable.
 */
export function roomConfigFromRow(row: unknown): RoomConfig {
  const parsed = RoomConfigRowSchema.safeParse(row ?? {});
  if (!parsed.success) return DEFAULT_ROOM_CONFIG;
  const mode = RoomModeSchema.safeParse(parsed.data.room_mode);
  const name = RoomDisplayNameSchema.safeParse(parsed.data.display_name);
  const video = VideoTransportSchema.safeParse(parsed.data.video_transport);
  const audio = AudioTransportSchema.safeParse(parsed.data.audio_transport);
  return {
    mode: mode.success ? mode.data : DEFAULT_ROOM_CONFIG.mode,
    displayName: name.success ? name.data : DEFAULT_ROOM_CONFIG.displayName,
    autostartTour: parsed.data.autostart_tour === true,
    videoTransport: video.success
      ? video.data
      : DEFAULT_ROOM_CONFIG.videoTransport,
    audioTransport: audio.success
      ? audio.data
      : DEFAULT_ROOM_CONFIG.audioTransport,
    // The one field whose default is true, so it is read as "anything but a
    // recorded false": a database without the column yet offers the journal,
    // which is what every room recorded before it did.
    journalVisibleToAll: parsed.data.journal_visible_to_all !== false,
    // Read the same way, for the same reason: on unless a false is recorded.
    voiceCleaning: parsed.data.voice_cleaning !== false,
  };
}

/**
 * What an administrator may say about a room, and how it is spelled in the row.
 *
 * `strict` on purpose: a field nobody recognises is a caller that believes it is
 * configuring something, and answering "unknown field" is kinder than accepting
 * the request and ignoring half of it.
 */
export const RoomConfigPatchSchema = z
  .object({
    mode: RoomModeSchema,
    displayName: RoomDisplayNameSchema.nullable(),
    autostartTour: z.boolean(),
  })
  .partial()
  .strict();

export type RoomConfigPatch = z.infer<typeof RoomConfigPatchSchema>;

/** The configuration as its columns, for a writer that speaks in columns. */
export interface RoomConfigColumns {
  room_mode?: RoomMode;
  display_name?: string | null;
  autostart_tour?: boolean;
  journal_visible_to_all?: boolean;
  voice_cleaning?: boolean;
  video_transport?: VideoTransport;
  audio_transport?: AudioTransport;
}

/** The columns a patch writes, named as the database names them. */
export function roomConfigRowPatch(patch: RoomConfigPatch): RoomConfigColumns {
  return {
    ...(patch.mode === undefined ? {} : { room_mode: patch.mode }),
    ...(patch.displayName === undefined
      ? {}
      : { display_name: patch.displayName }),
    ...(patch.autostartTour === undefined
      ? {}
      : { autostart_tour: patch.autostartTour }),
  };
}

/** A configuration with the patch applied, for the answer a caller reads back. */
export function applyRoomConfigPatch(
  config: RoomConfig,
  patch: RoomConfigPatch,
): RoomConfig {
  return {
    ...config,
    mode: patch.mode ?? config.mode,
    displayName:
      patch.displayName === undefined ? config.displayName : patch.displayName,
    autostartTour: patch.autostartTour ?? config.autostartTour,
  };
}
